import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'react-query';
import styled, { css } from 'styled-components';
import toast from 'react-hot-toast';
import {
  FaCheck,
  FaMapMarkerAlt,
  FaClock,
  FaBuilding,
  FaCalendarAlt,
  FaArrowLeft,
  FaHeart,
  FaRegHeart,
} from 'react-icons/fa';
import { coachingAPI } from '../services/api';
import {
  normalizeCoachDetail,
  normalizeOffering,
  groupSlotsByDate,
  formatSlotTimeLabel,
} from '../utils/coachCrsAdapter';
import { COACH_TIER_BADGE_STYLES } from '../utils/coachUiConstants';
import CoachAvatarImage from '../components/PlayerDashboard/CoachAvatarImage';
import CoachBannerImage from '../components/PlayerDashboard/CoachBannerImage';

const BRAND = { blue: '#043663', red: '#D52B1E' };
const LS_FAV = 'hps_coach_directory_favorites';

function loadFavorites() {
  try {
    const raw = localStorage.getItem(LS_FAV);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveFavorites(set) {
  localStorage.setItem(LS_FAV, JSON.stringify([...set]));
}

const PageShell = styled.div`
  width: 100%;
  max-width: 100%;
`;

const BackRow = styled.div`
  margin-bottom: 1rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${BRAND.blue};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const PageHeader = styled.header`
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-size: 1.65rem;
  font-weight: 700;
  color: #0f172a;
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #64748b;
  line-height: 1.5;
  max-width: 52rem;
`;

const AboutBlock = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
`;

const AboutMiniTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #0f172a;
`;

const AboutBody = styled.div`
  font-size: 0.9rem;
  line-height: 1.55;
  color: #475569;
  white-space: pre-wrap;
  ${(p) =>
    p.$clamp &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

const ViewMoreBtn = styled.button`
  margin-top: 0.65rem;
  border: none;
  background: none;
  padding: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${BRAND.blue};
  cursor: pointer;
  text-decoration: underline;
`;

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem 2rem;
  align-items: start;
  width: 100%;

  @media (min-width: 1080px) {
    grid-template-columns: minmax(0, 1fr) minmax(300px, 400px);
  }
`;

const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
`;

const Section = styled.section`
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(4, 54, 99, 0.08);
  padding: 1.35rem 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  ${(p) =>
    p.$flush &&
    css`
      padding: 0;
      overflow: hidden;
    `}
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
`;

const CoachProfileHitArea = styled.div`
  cursor: pointer;
  text-align: left;
  width: 100%;
  display: block;
  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }
`;

const BookingBanner = styled.div`
  position: relative;
  height: 220px;
  min-height: 180px;
  background: linear-gradient(135deg, #0c4a6e 0%, ${BRAND.blue} 55%, #1e3a5f 100%);
  overflow: hidden;
  @media (max-width: 480px) {
    height: 200px;
  }
`;

const BannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  pointer-events: none;
  z-index: 2;
  & > * {
    pointer-events: auto;
  }
`;

const AvailPill = styled.span`
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: ${(p) => (p.$today ? '#16a34a' : '#0f172a')};
  color: #fff;
`;

const FavBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${BRAND.red};
  flex-shrink: 0;
  &:hover {
    background: #fef2f2;
  }
`;

const CoachCardBody = styled.div`
  padding: 1rem 1.35rem 1.35rem;
`;

const CoachHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
`;

const SmallAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #e2e8f0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CoachMeta = styled.div`
  flex: 1;
  min-width: 200px;
`;

const CoachName = styled.div`
  font-weight: 700;
  font-size: 1.15rem;
  color: #0f172a;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const TierPill = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const Chip = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
`;

const PackageScroll = styled.div`
  display: flex;
  gap: 0.85rem;
  overflow-x: auto;
  padding-bottom: 0.35rem;
`;

const PackageCard = styled.button`
  flex: 0 0 220px;
  text-align: left;
  border-radius: 14px;
  padding: 1.05rem 1rem;
  border: 2px solid ${(p) => (p.$sel ? BRAND.blue : '#e2e8f0')};
  cursor: pointer;
  position: relative;
  transition: border-color 0.15s, background 0.15s, color 0.15s, box-shadow 0.15s;
  ${(p) =>
    p.$sel
      ? css`
          background: ${BRAND.blue};
          border-color: ${BRAND.blue};
          color: #fff;
          box-shadow: 0 8px 24px rgba(4, 54, 99, 0.25);
          .pkg-title,
          .pkg-fee {
            color: #fff;
          }
          .pkg-sub {
            color: rgba(255, 255, 255, 0.88);
          }
        `
      : css`
          background: #fff;
          color: #334155;
          .pkg-title,
          .pkg-fee {
            color: #0f172a;
          }
          .pkg-sub {
            color: #64748b;
          }
          &:hover {
            border-color: ${BRAND.blue};
          }
        `}
`;

const PkgCheck = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  ${(p) =>
    p.$sel
      ? css`
          background: #fff;
          color: ${BRAND.blue};
        `
      : css`
          opacity: 0;
        `}
`;

const PkgTitle = styled.div`
  font-weight: 700;
  font-size: 0.92rem;
  padding-right: 1.75rem;
`;

const PkgSub = styled.div`
  font-size: 0.8rem;
  margin-top: 0.3rem;
`;

const PkgFee = styled.div`
  margin-top: 0.7rem;
  font-size: 0.85rem;
  font-weight: 600;
`;

const DateStrip = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.35rem;
`;

const DateChip = styled.button`
  flex: 0 0 auto;
  min-width: 64px;
  padding: 0.55rem 0.65rem;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$sel ? BRAND.blue : '#e2e8f0')};
  background: ${(p) => (p.$sel ? BRAND.blue : '#fff')};
  color: ${(p) => (p.$sel ? '#fff' : '#334155')};
  cursor: pointer;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
`;

const TimeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
`;

const TimeChip = styled.button`
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$sel ? BRAND.blue : '#e2e8f0')};
  background: ${(p) => (p.$sel ? BRAND.blue : '#fff')};
  color: ${(p) => (p.$sel ? '#fff' : '#334155')};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
`;

const VenueScroll = styled.div`
  display: flex;
  gap: 0.85rem;
  overflow-x: auto;
`;

const VenueCard = styled.div`
  flex: 0 0 240px;
  border-radius: 14px;
  border: 2px solid ${(p) => (p.$sel ? BRAND.blue : '#e2e8f0')};
  padding: 0.85rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  ${(p) => p.$sel && `box-shadow: 0 8px 24px rgba(4, 54, 99, 0.12);`}
`;

const VenueName = styled.div`
  font-weight: 700;
  font-size: 0.92rem;
  color: #0f172a;
`;

const VenueMeta = styled.div`
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 0.35rem;
`;

const Summary = styled.aside`
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(4, 54, 99, 0.1);
  padding: 1.35rem;
  box-shadow: 0 4px 28px rgba(15, 23, 42, 0.07);
  width: 100%;
  max-width: 100%;
  @media (min-width: 1080px) {
    position: sticky;
    top: 80px;
  }
`;

const SumTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
`;

const SumCoach = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f5f9;
`;

const SumMiniAv = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SumLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.86rem;
  margin-bottom: 0.7rem;
  color: #334155;
  svg {
    margin-top: 3px;
    flex-shrink: 0;
    opacity: 0.75;
    color: ${BRAND.blue};
  }
`;

const SumLineLabel = styled.span`
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-bottom: 0.15rem;
`;

const SumLineValue = styled.span`
  display: block;
  font-weight: ${(p) => (p.$muted ? 500 : 600)};
  color: ${(p) => (p.$muted ? '#94a3b8' : '#0f172a')};
`;

const PriceBlock = styled.div`
  border-top: 1px solid #e5e7eb;
  margin-top: 1rem;
  padding-top: 1rem;
  font-size: 0.88rem;
  color: #64748b;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.45rem;
  gap: 1rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.85rem;
  font-size: 1.28rem;
  font-weight: 800;
  color: #0f172a;
`;

const ConfirmBtn = styled.button`
  width: 100%;
  margin-top: 1.25rem;
  padding: 0.9rem;
  border: none;
  border-radius: 12px;
  background: ${BRAND.red};
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
  &:hover:not(:disabled) {
    background: #b52217;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChangeLink = styled.button`
  display: block;
  width: 100%;
  margin-top: 0.75rem;
  border: none;
  background: none;
  color: ${BRAND.blue};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  text-align: center;
  text-decoration: underline;
`;

const TrustList = styled.ul`
  margin: 1.25rem 0 0;
  padding: 0;
  list-style: none;
  font-size: 0.78rem;
  color: #64748b;
  li {
    padding: 0.25rem 0;
    padding-left: 1.1rem;
    position: relative;
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #16a34a;
      font-weight: 700;
    }
  }
`;

const Disclaimer = styled.p`
  margin: 1rem 0 0;
  font-size: 0.72rem;
  color: #94a3b8;
  line-height: 1.45;
`;

const NotFound = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  max-width: 400px;
  margin: 0 auto;
  h1 {
    font-size: 1.25rem;
    margin: 0 0 0.5rem;
  }
  p {
    color: #64748b;
    margin: 0 0 1.25rem;
  }
`;

const NFBack = styled(Link)`
  display: inline-block;
  padding: 0.6rem 1.2rem;
  border-radius: 10px;
  background: ${BRAND.blue};
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    opacity: 0.92;
    color: #fff;
  }
`;

const Muted = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.88rem;
  color: #64748b;
`;

const EM = '—';

function formatDateChip(iso) {
  const d = new Date(iso + 'T12:00:00');
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return { day: days[d.getDay()], num: d.getDate() };
}

function formatLongDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

function dateRangeFromToday(days) {
  const a = new Date();
  const b = new Date();
  b.setDate(b.getDate() + days);
  return {
    from: a.toISOString().slice(0, 10),
    to: b.toISOString().slice(0, 10),
  };
}

export default function PlayerCoachBookingPage() {
  const { t } = useTranslation();
  const { coachId } = useParams();
  const packagesRef = useRef(null);
  const [templateId, setTemplateId] = useState('');
  const [dateKey, setDateKey] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [mobileClamp, setMobileClamp] = useState(false);
  const [favorites, setFavorites] = useState(() => loadFavorites());

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const fn = () => setMobileClamp(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  const range = useMemo(() => dateRangeFromToday(13), []);

  const {
    data: rawDetail,
    isLoading: detailLoading,
    isError: detailError,
  } = useQuery(
    ['coachingCoach', coachId],
    () => coachingAPI.getCoach(coachId),
    { enabled: Boolean(coachId), retry: false }
  );

  const coach = useMemo(() => (rawDetail ? normalizeCoachDetail(rawDetail) : null), [rawDetail]);

  const { data: rawOfferings = [], isLoading: offLoading } = useQuery(
    ['coachingOfferings', coachId],
    () => coachingAPI.getOfferings(coachId),
    { enabled: Boolean(coachId), staleTime: 60 * 1000 }
  );

  const offerings = useMemo(
    () => (Array.isArray(rawOfferings) ? rawOfferings.map(normalizeOffering) : []),
    [rawOfferings]
  );

  const { data: slotsPayload, isLoading: slotsLoading } = useQuery(
    ['coachingSlots', coachId, templateId, range.from, range.to],
    () =>
      coachingAPI.getAvailableSlots(coachId, {
        session_template_id: templateId,
        from: range.from,
        to: range.to,
      }),
    { enabled: Boolean(coachId && templateId), staleTime: 30 * 1000, retry: 1 }
  );

  const slots = slotsPayload?.slots || [];
  const scheduleTimezone = slotsPayload?.schedule_timezone || 'Europe/Zurich';
  const slotsMessage = slotsPayload?.message;

  const { slotsByDate, dates } = useMemo(() => groupSlotsByDate(slots), [slots]);

  const daySlots = dateKey ? slotsByDate[dateKey] || [] : [];

  const selectedOffering = offerings.find((o) => o.id === templateId);

  useEffect(() => {
    setDateKey('');
    setSelectedSlot(null);
    setSelectedVenue(null);
  }, [templateId]);

  useEffect(() => {
    setSelectedSlot(null);
    setSelectedVenue(null);
  }, [dateKey]);

  useEffect(() => {
    setSelectedVenue(null);
  }, [selectedSlot]);

  const coachFeeChf = selectedSlot ? (selectedSlot.coach_fee_cents || 0) / 100 : null;
  const courtFeeChf = selectedVenue ? (selectedVenue.court_fee_cents || 0) / 100 : null;
  const totalChf = selectedVenue ? (selectedVenue.total_cents || 0) / 100 : null;

  const canConfirm = Boolean(
    coach && templateId && selectedSlot && selectedVenue?.court_id && totalChf != null
  );

  const bookMutation = useMutation(
    async () => {
      const idempotency_key = `hps-${coachId}-${templateId}-${selectedSlot.starts_at}-${selectedVenue.court_id}-${Date.now()}`;
      return coachingAPI.bookFromOffering({
        session_template_id: templateId,
        starts_at: selectedSlot.starts_at,
        court_id: selectedVenue.court_id,
        idempotency_key,
      });
    },
    {
      onSuccess: async (res) => {
        if (res?.payment?.required && res?.order?.id) {
          try {
            const checkout = await coachingAPI.createCheckoutSession(res.order.id);
            if (checkout?.url) {
              window.location.href = checkout.url;
              return;
            }
          } catch (e) {
            toast.error(t('playerCoachBookingPage.toastCheckoutFail'));
            return;
          }
        }
        toast.success(t('playerCoachBookingPage.toastBookingCreated'));
      },
      onError: (e) => {
        const msg = e?.response?.data?.error || e?.message || t('playerCoachBookingPage.toastBookingFail');
        toast.error(msg);
      },
    }
  );

  const scrollToFirst = () => packagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const toggleFav = useCallback(
    (e) => {
      e.stopPropagation();
      if (!coach?.id) return;
      setFavorites((prev) => {
        const next = new Set(prev);
        if (next.has(coach.id)) next.delete(coach.id);
        else next.add(coach.id);
        saveFavorites(next);
        return next;
      });
    },
    [coach?.id]
  );

  if (detailError || (!detailLoading && !coach)) {
    return (
      <NotFound>
        <h1>{t('playerCoachBookingPage.notFoundTitle')}</h1>
        <p>{t('playerCoachBookingPage.notFoundBody')}</p>
        <NFBack to="/player/coaches">{t('playerCoachBookingPage.backDirectory')}</NFBack>
      </NotFound>
    );
  }

  if (detailLoading || !coach) {
    return (
      <PageShell>
        <Muted>{t('playerCoachBookingPage.loadingCoach')}</Muted>
      </PageShell>
    );
  }

  const tier = COACH_TIER_BADGE_STYLES[coach.coach_tier] || COACH_TIER_BADGE_STYLES.coach;
  const isFav = favorites.has(coach.id);
  const availToday = coach.is_available_booking === true;
  const sessionLabel = selectedOffering
    ? `${selectedOffering.label}, ${selectedOffering.subtitle}`
    : t('playerCoachBookingPage.selectPackage');
  const aboutText = (coach.about_me || '').trim() || t('playerCoachBookingPage.noBio');

  const dateTimeSummary = (() => {
    if (!dateKey) return { text: EM, muted: true };
    const longD = formatLongDate(dateKey);
    if (!selectedSlot) return { text: longD, muted: false };
    return { text: `${longD} · ${formatSlotTimeLabel(selectedSlot.starts_at)}`, muted: false };
  })();

  return (
    <PageShell>
      <BackRow>
        <BackLink to="/player/coaches">
          <FaArrowLeft aria-hidden />
          {t('playerCoachBookingPage.backAllCoaches')}
        </BackLink>
      </BackRow>

      <PageHeader>
        <PageTitle>{t('playerCoachBookingPage.pageTitle')}</PageTitle>
        <PageSubtitle>
          {t('playerCoachBookingPage.pageSubtitle', { tz: scheduleTimezone })}
        </PageSubtitle>
      </PageHeader>

      <PageGrid>
        <MainCol>
          <Section $flush>
            <CoachProfileHitArea
              role="button"
              tabIndex={0}
              onClick={scrollToFirst}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  scrollToFirst();
                }
              }}
              aria-label={t('playerCoachBookingPage.ariaContinueBook')}
            >
              <BookingBanner>
                <CoachBannerImage coachId={coach.id} hasBannerImage={coach.has_banner_image} />
                <BannerOverlay>
                  <AvailPill $today={availToday}>{coach.availability_label}</AvailPill>
                  <FavBtn
                    type="button"
                    onClick={toggleFav}
                    aria-label={isFav ? t('playerFindCoachPage.ariaRemoveFav') : t('playerFindCoachPage.ariaAddFav')}
                  >
                    {isFav ? <FaHeart /> : <FaRegHeart />}
                  </FavBtn>
                </BannerOverlay>
              </BookingBanner>
              <CoachCardBody>
                <CoachHeader>
                  <SmallAvatar>
                    <CoachAvatarImage
                      coachId={coach.id}
                      photoUrl={coach.profile_photo_url}
                      hasPhotoImage={coach.has_photo_image}
                      alt=""
                    />
                  </SmallAvatar>
                  <CoachMeta>
                    <CoachName>
                      {coach.display_name}
                      <TierPill $bg={tier.bg} $color={tier.color}>
                        {coach.coach_tier_label}
                      </TierPill>
                    </CoachName>
                    <ChipRow>
                      {[coach.coach_tier_label.split(' ')[0], ...coach.languages.slice(0, 2)].map((t) => (
                        <Chip key={t}>{t}</Chip>
                      ))}
                    </ChipRow>
                  </CoachMeta>
                </CoachHeader>
                <AboutBlock
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <AboutMiniTitle>{t('playerCoachBookingPage.aboutTitle')}</AboutMiniTitle>
                  <AboutBody $clamp={mobileClamp && !aboutExpanded}>{aboutText}</AboutBody>
                  {mobileClamp && aboutText.length > 120 && (
                    <ViewMoreBtn
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAboutExpanded((x) => !x);
                      }}
                    >
                      {aboutExpanded ? t('playerCoachBookingPage.viewLess') : t('playerCoachBookingPage.viewMore')}
                    </ViewMoreBtn>
                  )}
                </AboutBlock>
              </CoachCardBody>
            </CoachProfileHitArea>
          </Section>

          <Section ref={packagesRef}>
            <SectionTitle>{t('playerCoachBookingPage.choosePackage')}</SectionTitle>
            {offLoading ? (
              <Muted>{t('playerCoachBookingPage.loadingPackages')}</Muted>
            ) : offerings.length === 0 ? (
              <Muted>{t('playerCoachBookingPage.noPackages')}</Muted>
            ) : (
              <PackageScroll>
                {offerings.map((p) => (
                  <PackageCard
                    key={p.id}
                    type="button"
                    $sel={p.id === templateId}
                    onClick={() => setTemplateId(p.id)}
                  >
                    <PkgCheck $sel={p.id === templateId}>
                      <FaCheck />
                    </PkgCheck>
                    <PkgTitle className="pkg-title">{p.label}</PkgTitle>
                    <PkgSub className="pkg-sub">{p.subtitle}</PkgSub>
                    <PkgSub className="pkg-sub">
                      {p.duration_minutes} min · {p.club_name || p.difficulty_copy}
                    </PkgSub>
                    <PkgFee className="pkg-fee">{t('playerCoachBookingPage.coachFeeLabel', { amount: p.coach_fee_chf.toFixed(2) })}</PkgFee>
                  </PackageCard>
                ))}
              </PackageScroll>
            )}
          </Section>

          <Section>
            <SectionTitle>{t('playerCoachBookingPage.pickTime')}</SectionTitle>
            {!templateId ? (
              <Muted>{t('playerCoachBookingPage.selectPackageForSlots')}</Muted>
            ) : slotsLoading ? (
              <Muted>{t('playerCoachBookingPage.loadingSlots')}</Muted>
            ) : slotsMessage && slots.length === 0 ? (
              <Muted>{slotsMessage}</Muted>
            ) : dates.length === 0 ? (
              <Muted>{t('playerCoachBookingPage.noSlotsRange')}</Muted>
            ) : (
              <>
                <DateStrip>
                  {dates.map((d) => {
                    const { day, num } = formatDateChip(d);
                    return (
                      <DateChip
                        key={d}
                        type="button"
                        $sel={d === dateKey}
                        onClick={() => setDateKey(d)}
                      >
                        {day}
                        <br />
                        {num}
                      </DateChip>
                    );
                  })}
                </DateStrip>
                <TimeRow>
                  {dateKey ? (
                    daySlots.map((s, idx) => (
                        <TimeChip
                          key={`${s.starts_at}-${idx}`}
                          type="button"
                          $sel={selectedSlot === s}
                          onClick={() => setSelectedSlot(s)}
                        >
                          {formatSlotTimeLabel(s.starts_at)}
                        </TimeChip>
                    ))
                  ) : (
                    <Muted>{t('playerCoachBookingPage.chooseDate')}</Muted>
                  )}
                </TimeRow>
              </>
            )}
          </Section>

          <Section>
            <SectionTitle>{t('playerCoachBookingPage.selectCourt')}</SectionTitle>
            {!selectedSlot ? (
              <Muted>{t('playerCoachBookingPage.chooseSlotForCourts')}</Muted>
            ) : !(selectedSlot.venues && selectedSlot.venues.length) ? (
              <Muted>{t('playerCoachBookingPage.noCourtsSlot')}</Muted>
            ) : (
              <VenueScroll>
                {selectedSlot.venues.map((v) => (
                  <VenueCard
                    key={v.court_id}
                    $sel={selectedVenue?.court_id === v.court_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedVenue(v)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedVenue(v);
                      }
                    }}
                  >
                    <VenueName>{v.court_name || t('playerCoachBookingPage.courtFallback')}</VenueName>
                    <VenueMeta>
                      {t('playerCoachBookingPage.courtFeesLine', {
                        court: ((v.court_fee_cents || 0) / 100).toFixed(2),
                        total: ((v.total_cents || 0) / 100).toFixed(2),
                      })}
                    </VenueMeta>
                  </VenueCard>
                ))}
              </VenueScroll>
            )}
          </Section>
        </MainCol>

        <Summary aria-live="polite">
          <SumTitle>{t('playerCoachBookingPage.summaryTitle')}</SumTitle>
          <SumCoach>
            <SumMiniAv>
              <CoachAvatarImage
                coachId={coach.id}
                photoUrl={coach.profile_photo_url}
                hasPhotoImage={coach.has_photo_image}
                alt=""
              />
            </SumMiniAv>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>
                {coach.display_name}
              </div>
              <SumLineValue $muted={!selectedOffering} style={{ fontSize: '0.84rem', marginTop: '0.15rem' }}>
                {sessionLabel}
              </SumLineValue>
            </div>
          </SumCoach>

          <SumLine>
            <FaClock />
            <div>
              <SumLineLabel>{t('playerCoachBookingPage.duration')}</SumLineLabel>
              <SumLineValue $muted={!selectedOffering}>
                {selectedOffering ? t('playerCoachBookingPage.minutes', { n: selectedOffering.duration_minutes }) : EM}
              </SumLineValue>
            </div>
          </SumLine>

          <SumLine>
            <FaCalendarAlt />
            <div>
              <SumLineLabel>{t('playerCoachBookingPage.dateTime')}</SumLineLabel>
              <SumLineValue $muted={dateTimeSummary.muted}>{dateTimeSummary.text}</SumLineValue>
            </div>
          </SumLine>

          <SumLine>
            <FaBuilding />
            <div>
              <SumLineLabel>{t('playerCoachBookingPage.club')}</SumLineLabel>
              <SumLineValue $muted={!selectedOffering?.club_name}>{selectedOffering?.club_name || EM}</SumLineValue>
            </div>
          </SumLine>

          <SumLine>
            <FaMapMarkerAlt />
            <div>
              <SumLineLabel>{t('playerCoachBookingPage.court')}</SumLineLabel>
              <SumLineValue $muted={!selectedVenue}>
                {selectedVenue ? selectedVenue.court_name || t('playerCoachBookingPage.courtFallback') : EM}
              </SumLineValue>
            </div>
          </SumLine>

          <PriceBlock>
            <PriceRow>
              <span>{t('playerCoachBookingPage.coachFee')}</span>
              <span style={{ fontWeight: 600, color: '#0f172a' }}>
                {coachFeeChf != null ? `CHF ${coachFeeChf.toFixed(2)}` : EM}
              </span>
            </PriceRow>
            <PriceRow>
              <span>{t('playerCoachBookingPage.courtFee')}</span>
              <span style={{ fontWeight: 600, color: '#0f172a' }}>
                {courtFeeChf != null ? `CHF ${courtFeeChf.toFixed(2)}` : EM}
              </span>
            </PriceRow>
            <TotalRow>
              <span>{t('playerCoachBookingPage.total')}</span>
              <span>{totalChf != null ? `CHF ${totalChf.toFixed(2)}` : EM}</span>
            </TotalRow>
          </PriceBlock>

          <ConfirmBtn
            type="button"
            disabled={!canConfirm || bookMutation.isLoading}
            onClick={() => bookMutation.mutate()}
          >
            {bookMutation.isLoading ? t('playerCoachBookingPage.booking') : t('playerCoachBookingPage.confirmBooking')}
          </ConfirmBtn>
          <ChangeLink type="button" onClick={scrollToFirst}>
            {t('playerCoachBookingPage.changeSelection')}
          </ChangeLink>
          <TrustList>
            <li>{t('playerCoachBookingPage.trustSecure')}</li>
            <li>{t('playerCoachBookingPage.trustInstant')}</li>
            <li>{t('playerCoachBookingPage.trustReserved')}</li>
          </TrustList>
          <Disclaimer>
            {t('playerCoachBookingPage.disclaimer', { tz: scheduleTimezone })}
          </Disclaimer>
        </Summary>
      </PageGrid>
    </PageShell>
  );
}
